import { useState, useMemo, useEffect } from "react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../components/LoadingSpinner";
import { CustomAlert } from "../components/CustomAlert";
import SimpleTable from "../components/SimpleTable";
import MoreInfoIcon from "../icons/MoreInfoIcon";
import type { SweetAlertIcon } from "sweetalert2";
import type { TFunction } from "i18next";
import { useYardStore } from "../../../store/oasis/yardsStore";
import HistoryButton from "./components/HistoryButton";
// Contexto
import { useDarkMode } from "../../../context/ThemeContext";
import {
  ListContainer,
  NavbarListContainer,
  Container1,
  Path,
  NavbarTitle,
  TableContainer,
} from "./styles";
import ModalAccessHistory from "./components/ModalAccessHistory";
import { fetchAccessHistory } from "../../../store/oasis/accessStore";
import { accessTableColumns } from "./functions";
import { fetchAccesses } from "../../../store/oasis/accessStore";

const itemsPerPage = 10;

interface Access {
  id: string;
  name: string;
  type: string;
  status: string;
  [key: string]: any;
}

interface NewAccessListItem extends Access {
  ID: () => JSX.Element;
  history: () => JSX.Element;
}
interface AccessHistoryItem {
  page: any[];
  lastDocId: string;
  totalPages: number;
}

function AccessList() {
  const { t } = useTranslation("accessList");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{
    visible: boolean;
    title?: string;
    text?: string;
    icon?: SweetAlertIcon;
    confirmButtonText?: string;
    confirmButtonColor?: string;
    onConfirm?: () => void;
    showCancelButton?: boolean;
    cancelButtonText?: string;
    cancelButtonColor?: string;
    onCancel?: () => void;
  }>({ visible: false });

  // Modo oscuro
  const { isDarkMode } = useDarkMode();

  // Estados globales
  const { yardSelected } = useYardStore();

  const [accessList, setAccessList] = useState<Access[]>([]);
  const [pagination, setPagination] = useState(1);
  const [previusLastDocID, setPreviusLastDocID] = useState<string | null>(null);
  const [currentLastDocID, setCurrentLastDocID] = useState("");
  const [accessSelected, setAccessSelected] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [accessHistory, setAccessHistory] = useState<AccessHistoryItem[]>([
    { page: [], lastDocId: "", totalPages: 1 },
  ]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const newAccessList: NewAccessListItem[] | undefined = useMemo(
    () =>
      accessList[pagination - 1]?.page?.map(
        (item: Access): NewAccessListItem => ({
          ...item,
          ID: () => <MoreInfoIcon colorIcon="#4d5254" msg={item.id} />,
          history: () => {
            const handleClick = () => {
              handleShowHistory(item);
              setShowModal(true);
            };
            return (
              <HistoryButton handleClick={handleClick} loading={loading} />
            );
          },
        })
      ),
    [accessList, pagination]
  );

  const handleShowHistory = async (item: Access) => {
    setAccessSelected(item);
    setShowModal(true);
    setLoadingHistory(true);
    try {
      if (!yardSelected?.id) {
        throw new Error("No yard selected");
      }
      const historyData = await fetchAccessHistory({
        yardId: yardSelected.id,
        accessId: item.id,
        itemsPerPage: 6,
      });
      setAccessHistory([historyData]);
    } catch (error: any) {
      let errorMsg = "";
      if (!navigator.onLine) {
        errorMsg = t("noInternetConnection") || "Sin conexión a internet.";
      } else if (
        errorMsg.includes("Failed to fetch") ||
        errorMsg.includes("ERR_CONNECTION_REFUSED") ||
        (error?.message &&
          (error.message.includes("Failed to fetch") ||
            error.message.includes("ERR_CONNECTION_REFUSED")))
      ) {
        errorMsg = `${
          t("internalServerError") || "Error interno del servidor."
        }\n(TypeError: Failed to fetch)`;
      } else if (error?.message) {
        errorMsg = `${t("internalServerError")}: ${error.message}`;
      } else {
        errorMsg = `${t("internalServerError")}: ${String(error)}`;
      }
      setAlert({
        visible: true,
        title: t("error"),
        text: errorMsg,
        icon: "error",
        confirmButtonText: t("closeButton"),
        confirmButtonColor: "#1C93FF",
        onConfirm: () => setAlert({ visible: false }),
      });
    } finally {
      setLoadingHistory(false);
    }
  };

  // Cambia de página

  const changePage = async (page: string) => {
    setLoading(true);
    try {
      const data = await fetchAccesses({
        yardId: yardSelected?.id ?? "",
        itemsPerPage,
        lastDocId: page,
      });
      setAccessList((prev) => {
        const updated = [...prev];
        updated[pagination] = data;
        return updated;
      });
      setCurrentLastDocID(data.lastDocId || "");
    } catch (error: any) {
      let errorMsg = "";
      if (!navigator.onLine) {
        errorMsg = t("noInternetConnection") || "Sin conexión a internet.";
      } else if (
        error?.message?.includes("Failed to fetch") ||
        String(error).includes("Failed to fetch")
      ) {
        errorMsg = `${
          t("internalServerError") || "Error interno del servidor."
        }\n(TypeError: Failed to fetch)`;
      } else if (error?.message) {
        errorMsg = `${t("internalServerError")}: ${error.message}`;
      } else {
        errorMsg = `${t("internalServerError")}: ${String(error)}`;
      }
      setAlert({
        visible: true,
        title: t("error"),
        text: errorMsg,
        icon: "error",
        confirmButtonText: t("closeButton"),
        confirmButtonColor: "#1C93FF",
        onConfirm: () => setAlert({ visible: false }),
      });
    } finally {
      setLoading(false);
    }
  };

  // Cambia a la siguiente página
  const nextPage = () => {
    setPreviusLastDocID(currentLastDocID);
    changePage(currentLastDocID);
    setPagination(pagination + 1);
  };

  // Cambia a la página anterior
  const previusPage = () => {
    setPagination(pagination - 1);
    setCurrentLastDocID(previusLastDocID ?? "");
    setPreviusLastDocID(
      accessList[pagination - 2]?.lastDocId
        ? accessList[pagination - 2]?.lastDocId
        : null
    );
    changePage(accessList[pagination - 3]?.lastDocId);
  };

  // EFECTOS

  // Cargar la lista de accesos
  useEffect(() => {
    if (!yardSelected?.id) return;
    const loadAccesses = async () => {
      setLoading(true);
      try {
        const data = await fetchAccesses({
          yardId: yardSelected.id || "",
          itemsPerPage,
        });
        setAccessList([data]);
      } catch (error: any) {
        let errorMsg = "";
        if (!navigator.onLine) {
          errorMsg = t("noInternetConnection") || "Sin conexión a internet.";
        } else if (
          (error?.message &&
            (error.message.includes("Failed to fetch") ||
              error.message.includes("ERR_CONNECTION_REFUSED"))) ||
          String(error).includes("Failed to fetch") ||
          String(error).includes("ERR_CONNECTION_REFUSED")
        ) {
          errorMsg = t("internalServerError") || "Error interno del servidor.";
        } else if (error?.message) {
          errorMsg = `${t("internalServerError")}: ${error.message}`;
        } else {
          errorMsg = `${t("internalServerError")}: ${String(error)}`;
        }
        setAlert({
          visible: true,
          title: t("error"),
          text: errorMsg,
          icon: "error",
          confirmButtonText: t("closeButton"),
          confirmButtonColor: "#1C93FF",
          onConfirm: () => setAlert({ visible: false }),
        });
      } finally {
        setLoading(false);
      }
    };
    loadAccesses();
  }, [yardSelected?.id]);

  return (
    <ListContainer>
      {showModal && (
        <ModalAccessHistory
          data={accessSelected}
          isLoadingUser={loadingHistory}
          showDetails={true}
          actiocCloseModal={() => {
            setShowModal(false);
            setAccessSelected({});
            setAccessHistory([]);
          }}
          t={t}
          accessHistory={accessHistory} // <-- pasa el historial
          setAccessHistory={setAccessHistory}
        />
      )}
      {loading && <LoadingSpinner />}
      {alert.visible && (
        <CustomAlert
          title={alert.title || ""}
          text={alert.text || ""}
          icon={alert?.icon || "error"}
          confirmButtonText={alert.confirmButtonText}
          confirmButtonColor={alert.confirmButtonColor}
          onConfirm={alert.onConfirm}
          showCancelButton={alert.showCancelButton}
          cancelButtonText={alert.cancelButtonText}
          cancelButtonColor={alert.cancelButtonColor}
          onCancel={alert.onCancel}
        />
      )}
      {/* -------------------------------- Navbar -------------------------------- */}
      <NavbarListContainer id="navbar-access">
        <Container1>
          <Path>{`${t("security")} / ${t("accesses")}`}</Path>
          <NavbarTitle>{t("accesses")}</NavbarTitle>
        </Container1>
      </NavbarListContainer>
      {/* -------------------------------- Tabla -------------------------------- */}
      <TableContainer
        className={isDarkMode ? "bg-dark-surface rounded-xl" : "bg-white"}
      >
        <div className="w-full rounded-xl overflow-hidden" id="access-table">
          <SimpleTable
            data={newAccessList ?? []}
            tableColumns={() => accessTableColumns(t as TFunction)}
            totalPages={accessList[0]?.totalPages}
            pagination={pagination}
            nextPage={nextPage}
            previusPage={previusPage}
            pageRenderingType="api"
            t={t}
            defaultText={t("NoAccess")}
            disabled={false}
            isDarkMode={isDarkMode}
          />
        </div>
      </TableContainer>
    </ListContainer>
  );
}

export default AccessList;
